import React, { useState } from 'react';
import { Copy, KeyRound, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { adminStoresAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';

/*
  El acceso de caja de una tienda, dentro del editor de tiendas.

  Son dos cosas que se entregan juntas y viven distinto: el **enlace**, que se
  puede volver a consultar aquí siempre, y el **PIN**, que solo se ve en el
  momento de generarlo porque en la base queda hasheado. Esa asimetría se dice
  en pantalla, porque la trampa evidente es cerrar el modal antes de anotarlo.

  Rotar el PIN cierra las cajas que estén abiertas con el anterior. Es el
  comportamiento que se quiere —para eso se rota— pero hay que avisarlo: si se
  hace un sábado a mediodía, cada caja de esa tienda va a tener que teclear el
  nuevo antes de seguir atendiendo.
*/
const StoreAccessPanel = ({ store, onCambio }) => {
  const [pinVisible, setPinVisible] = useState(null);
  const [rotando, setRotando] = useState(false);

  const enlace = store?.accessUrl || (store?.accessSlug ? `${window.location.origin}/t/${store.accessSlug}` : null);
  const tienePin = Boolean(store?.hasAccessPin);

  const copiar = async (texto, queEs) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast({ title: `${queEs} copiado` });
    } catch (e) {
      toast({ title: 'No se pudo copiar', description: texto, variant: 'destructive' });
    }
  };

  const rotar = async () => {
    if (tienePin && !window.confirm(
      `Se generará un PIN nuevo para ${store.name}.\n\nLas cajas que estén abiertas con el PIN actual se cerrarán y tendrán que escribir el nuevo. ¿Seguimos?`
    )) {
      return;
    }

    setRotando(true);
    try {
      const res = await adminStoresAPI.rotateAccessPin(store.id);
      setPinVisible(res.data.pin);
      onCambio?.();
    } catch (err) {
      toast({
        title: 'No se pudo generar el PIN',
        description: err.message || 'Intenta de nuevo',
        variant: 'destructive',
      });
    } finally {
      setRotando(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-2xl p-5 space-y-4 bg-gray-50/60">
      <div className="flex items-start gap-3">
        <KeyRound className="w-5 h-5 text-gray-700 mt-0.5" />
        <div>
          <h4 className="font-semibold text-gray-900">Acceso de caja</h4>
          <p className="text-gray-500 text-sm">
            Con esto la tienda escanea los códigos de los clientes. El enlace identifica la tienda; el PIN es lo único
            que se teclea en caja.
          </p>
        </div>
      </div>

      {enlace ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-700">Enlace de la caja</p>
          <div className="flex gap-2">
            <code className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 break-all">
              {enlace}
            </code>
            <Button
              type="button"
              variant="outline"
              onClick={() => copiar(enlace, 'Enlace')}
              className="border-gray-200 text-gray-700 bg-white hover:bg-gray-100 rounded-xl shrink-0"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Guarda la tienda para que se le genere su enlace de caja.</p>
      )}

      {pinVisible ? (
        <div className="bg-white border-2 border-gray-900 rounded-xl p-4 space-y-2">
          <p className="text-xs font-medium text-gray-700">PIN nuevo — anótalo ahora</p>
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold tracking-[0.3em] text-gray-900">{pinVisible}</span>
            <Button
              type="button"
              variant="outline"
              onClick={() => copiar(pinVisible, 'PIN')}
              className="border-gray-200 text-gray-700 bg-white hover:bg-gray-100 rounded-xl"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-gray-500 text-xs">
            No se vuelve a mostrar: se guarda cifrado. Si se pierde, se genera otro desde aquí.
          </p>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            {tienePin ? (
              <span className="text-gray-600">
                PIN configurado
                {store?.accessPinSetAt && ` · desde el ${new Date(store.accessPinSetAt).toLocaleDateString('es-MX')}`}
              </span>
            ) : (
              <span className="flex items-center gap-2 text-amber-700">
                <ShieldAlert className="w-4 h-4" />
                Sin PIN: esta caja todavía no puede canjear
              </span>
            )}
          </div>

          <Button
            type="button"
            onClick={rotar}
            disabled={rotando}
            className="bg-black text-white hover:bg-black/90 rounded-full"
          >
            {rotando ? <Loader2 className="w-4 h-4 animate-spin" /> : tienePin ? 'Generar PIN nuevo' : 'Generar PIN'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default StoreAccessPanel;
